/**
 * Common utility functions shared across the application
 * ES6 class-based implementation for better organization and maintainability
 */

import { translator } from "../translation/translator";
import { phpUrl } from "../core/php-data-access";
import { CommonUtilsListener } from "./common-utils-listener";
import { FormBase } from "./form-base-form";

type UnknownRecord = Record<string, unknown>;

type LocaleFormatStyle = "short" | "medium" | "long";

interface TextToggleOptions {
    textElementId: string;
    buttonId: string;
    maxLength?: number;
    buttonTextSelector?: string;
}

export const HtmlInsertPosition = {
    BEFORE_END: "beforeend" as InsertPosition,
    AFTER_BEGIN: "afterbegin" as InsertPosition,
    BEFORE_BEGIN: "beforebegin" as InsertPosition,
};

class CommonUtilsFormBase extends FormBase {
    public getMeta(root: ParentNode, name: string): HTMLMetaElement | null {
        return this.queryElement(root, `meta[name="${name}"]`, HTMLMetaElement);
    }

    public getByDataRole<TElement extends Element = HTMLElement>(
        role: string,
        ctor?: abstract new (...args: never[]) => TElement,
    ): TElement | null {
        return this.queryElement(document, `[data-role="${role}"]`, ctor);
    }

    public getRetryButton(root: ParentNode): HTMLButtonElement | null {
        return this.queryElement(root, ".retry-btn", HTMLButtonElement);
    }

    public getButtonTextElement(root: ParentNode, selector: string): HTMLElement | null {
        return this.queryElement(root, selector, HTMLElement);
    }
}

const commonUtilsFormBase = new CommonUtilsFormBase();

export class CommonUtils {
    private static getElementByDataRole<TElement extends HTMLElement = HTMLElement>(
        role: string,
        ctor?: abstract new (...args: never[]) => TElement,
    ): TElement | null {
        const element = commonUtilsFormBase.getByDataRole(role, ctor);
        if (!element) {
            return null;
        }

        if (!ctor) {
            return element as TElement;
        }

        return element instanceof ctor ? element : null;
    }

    public static isMobileViewport(maxWidth = 768): boolean {
        const width = Number(maxWidth);
        const breakpoint = Number.isFinite(width) ? Math.max(0, Math.trunc(width)) : 768;

        try {
            if (typeof globalThis?.matchMedia === "function") {
                return globalThis.matchMedia(`(max-width: ${breakpoint}px)`).matches;
            }
        } catch (_error) {
            // Fall back to innerWidth check.
        }

        return typeof globalThis?.innerWidth === "number" ? globalThis.innerWidth <= breakpoint : false;
    }

    public static setHtml(container: Element | null | undefined, html: string): void {
        if (!(container instanceof HTMLElement)) return;
        container.innerHTML = String(html || "");
    }

    public static insertHtml(
        container: Element | null | undefined,
        html: string,
        position: InsertPosition = HtmlInsertPosition.BEFORE_END,
    ): void {
        if (!(container instanceof HTMLElement)) return;
        container.insertAdjacentHTML(position, String(html || ""));
    }

    public static setCommentTextWithLineBreaks(element: Element | null | undefined, content: string): void {
        if (!(element instanceof HTMLElement)) return;
        element.textContent = String(content || "");
    }

    public static toInt(value: unknown, fallback = 0): number {
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? Math.trunc(numberValue) : fallback;
    }

    public static toNumberOrNull(value: unknown): number | null {
        if (value === null || value === undefined || value === "") return null;
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? numberValue : null;
    }

    public static resolvePostImageUrl(boardId: unknown, userId: unknown, filename: unknown): string | null {
        const normalizedBoardId = typeof boardId === "string" || typeof boardId === "number" ? String(boardId) : "";
        const normalizedUserId = this.toNumberOrNull(userId);
        const normalizedFilename = typeof filename === "string" ? filename : "";

        if (!normalizedBoardId || normalizedUserId === null || !normalizedFilename) {
            return null;
        }

        return phpUrl.postImage(normalizedBoardId, normalizedUserId, normalizedFilename);
    }

    public static resolvePostVideoUrl(boardId: unknown, userId: unknown, filename: unknown): string | null {
        const normalizedBoardId = typeof boardId === "string" || typeof boardId === "number" ? String(boardId) : "";
        const normalizedUserId = this.toNumberOrNull(userId);
        const normalizedFilename = typeof filename === "string" ? filename : "";

        if (!normalizedBoardId || normalizedUserId === null || !normalizedFilename) {
            return null;
        }

        return phpUrl.postVideo(normalizedBoardId, normalizedUserId, normalizedFilename);
    }

    public static isRecord(value: unknown): value is UnknownRecord {
        return !!value && typeof value === "object" && !Array.isArray(value);
    }

    public static extractItems(payload: unknown): UnknownRecord[] {
        const root = this.asRecord(payload);
        const data = root ? root.data : null;
        if (Array.isArray(data)) return data.filter((item): item is UnknownRecord => this.isRecord(item));
        if (this.isRecord(data)) {
            const dataRecord = this.asRecord(data);
            if (!dataRecord) return [];
            if (Array.isArray(dataRecord.items)) {
                return dataRecord.items.filter((item): item is UnknownRecord => this.isRecord(item));
            }

            if (this.isRecord(dataRecord.data)) {
                const nested = this.asRecord(dataRecord.data);
                if (!nested) return [];
                if (Array.isArray(nested.items)) {
                    return nested.items.filter((item): item is UnknownRecord => this.isRecord(item));
                }
            }
        }
        return [];
    }

    public static asRecord(value: unknown): UnknownRecord | null {
        return this.isRecord(value) ? value : null;
    }

    public static formatAverageRating(value: unknown, decimals = 2, nullText = "--"): string {
        if (value === null || value === undefined) return nullText;

        const numberValue = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(numberValue)) return nullText;

        return numberValue.toFixed(decimals);
    }

    public static formatDate(dateString: string, locale: string | null = null, format: string | null = null): string {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            const userLocale = locale || navigator.language || "th-TH";
            const formatOptions: Intl.DateTimeFormatOptions = format
                ? this.parseCustomFormat(format)
                : {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                  };

            return date.toLocaleDateString(userLocale, formatOptions);
        } catch (error) {
            console.error("Error formatting date:", error);
            return dateString;
        }
    }

    public static parseCustomFormat(format: string): Intl.DateTimeFormatOptions {
        const options: Intl.DateTimeFormatOptions = {};

        if (format.includes("Y")) options.year = "numeric";
        if (format.includes("m")) options.month = "2-digit";
        if (format.includes("d")) options.day = "2-digit";
        if (format.includes("H")) options.hour = "2-digit";
        if (format.includes("i")) options.minute = "2-digit";
        if (format.includes("s")) options.second = "2-digit";

        return options;
    }

    public static escapeHtml(text: string): string {
        if (!text) return "";

        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
    }

    public static escapeHtmlAttribute(value: unknown): string {
        return String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

    public static decodeHtmlEntities(value: unknown): string {
        const text = String(value ?? "");
        if (!text.includes("&")) return text;

        const parser = document.createElement("textarea");
        parser.innerHTML = text;
        return parser.value;
    }

    public static truncateText(text: string, maxLength = 150): string {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + "...";
    }

    public static getAppBasePath(): string {
        const metaTag = commonUtilsFormBase.getMeta(document, "app-base-path");
        let basePath = metaTag?.getAttribute("content");

        if (!basePath) {
            return "";
        }

        if (!basePath.startsWith("/")) {
            basePath = `/${basePath}`;
        }

        return basePath.replace(/\/+$/, "");
    }

    public static resolveUrl(url: string): string {
        if (!url) {
            return url;
        }

        if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("//")) {
            return url;
        }

        const basePath = this.getAppBasePath();
        if (!basePath) {
            return url;
        }

        if (url === basePath || url.startsWith(`${basePath}/`)) {
            return url;
        }

        if (url.startsWith("/")) {
            return `${basePath}${url}`;
        }

        return url;
    }

    /**
     * Prefers the page the user came from (document.referrer) over a
     * hardcoded fallback, so actions like "delete" return to the list/search
     * screen the user was actually on (including its search query) instead
     * of a generic index page. Only used when the referrer is same-origin —
     * an external or empty referrer falls back.
     */
    public static resolveReturnUrl(fallbackUrl: string): string {
        const referrer = document.referrer;
        if (!referrer) {
            return fallbackUrl;
        }

        try {
            const referrerUrl = new URL(referrer);
            if (referrerUrl.origin !== window.location.origin) {
                return fallbackUrl;
            }
            return referrerUrl.href;
        } catch (_error) {
            return fallbackUrl;
        }
    }

    public static getTranslatedMessage(key: string, fallback = ""): string {
        if (translator && typeof translator.getTranslation === "function") {
            const translated = translator.getTranslation(key);
            if (translated && translated !== key) {
                return translated;
            }
        }

        return fallback || key;
    }

    public static showError(
        container: HTMLElement,
        message = "",
        retryCallback: ((event: Event) => void) | null = null,
    ): HTMLDivElement {
        const errorMessage = message || this.getTranslatedMessage("modal_error", "เกิดข้อผิดพลาดในการโหลดข้อมูล");

        const error = document.createElement("div");
        error.className = "modal-error";
        error.innerHTML = `
            <p>${this.escapeHtml(errorMessage)}</p>
            ${retryCallback ? `<button type="button" class="btn btn-primary retry-btn" data-translate="modal_retry">${this.getTranslatedMessage("modal_retry", "ลองใหม่")}</button>` : ""}
        `;

        if (retryCallback) {
            const retryBtn = commonUtilsFormBase.getRetryButton(error);
            if (retryBtn && typeof retryCallback === "function") {
                CommonUtilsListener.bindRetryClick(retryBtn, (event) => {
                    retryCallback(event);
                });
            }
        }

        container.appendChild(error);
        return error;
    }

    public static utcToLocale(
        dateString: string,
        locale: string | null = null,
        style: LocaleFormatStyle = "short",
    ): string {
        if (!dateString) return "";

        try {
            const userLocale = locale || navigator.language || "th-TH";
            const primaryLocale = userLocale.toLowerCase().split("-")[0].split("_")[0];
            const date = new Date(dateString);
            const formatOptions = this.getLocaleFormatOptions(primaryLocale, style);

            return date.toLocaleDateString(userLocale, formatOptions);
        } catch (error) {
            console.error("Error converting UTC to locale:", error);
            return dateString;
        }
    }

    public static getLocaleFormatOptions(
        locale: string,
        style: LocaleFormatStyle = "short",
    ): Intl.DateTimeFormatOptions {
        const baseOptions: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        };

        if (style === "medium") {
            baseOptions.month = "short";
        } else if (style === "long") {
            baseOptions.month = "long";
            baseOptions.weekday = "long";
        }

        switch (locale) {
            case "us":
            case "ph":
                baseOptions.hour12 = true;
                break;
            case "ja":
            case "ko":
            case "zh":
                if (style === "medium") {
                    baseOptions.month = "long";
                }
                break;
            default:
                break;
        }

        return baseOptions;
    }

    public static initTextToggle(options: TextToggleOptions): (() => void) | null {
        const { textElementId, buttonId, maxLength = 150, buttonTextSelector = "span[data-translate]" } = options;

        const textElement = this.getElementByDataRole(textElementId);
        const button = this.getElementByDataRole(buttonId);

        if (!textElement || !button) {
            console.warn(`Text toggle initialization failed: elements not found (${textElementId}, ${buttonId})`);
            return null;
        }

        const fullText = textElement.textContent?.trim() || "";

        if (fullText.length <= maxLength) {
            return null;
        }

        let isExpanded = false;
        const shortText = fullText.substring(0, maxLength) + "...";

        textElement.textContent = shortText;
        button.style.display = "inline-flex";

        const toggleFunction = (): void => {
            isExpanded = !isExpanded;
            textElement.textContent = isExpanded ? fullText : shortText;
            button.classList.toggle("expanded", isExpanded);

            const buttonTextElement = commonUtilsFormBase.getButtonTextElement(button, buttonTextSelector);
            if (buttonTextElement) {
                const translationKey = isExpanded ? "show_less" : "show_more";
                buttonTextElement.setAttribute("data-translate", translationKey);

                if (translator && typeof translator.getTranslation === "function") {
                    buttonTextElement.textContent = translator.getTranslation(translationKey);
                }
            }
        };

        return toggleFunction;
    }
}

export default CommonUtils;
